import Big from 'big.js';
import axios, { AxiosResponse } from 'smg/axios';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as session from 'smg/customer-api/session';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import { ColumnStats } from 'smg/table/table';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as payments from '../payments';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    if (await session.Redirect(undefined, Relref('/login'))) {
        return;
    }

    const loader = document.querySelector('#dashboard-loader') as HTMLElement;
    const accountBalance = document.querySelector('#account-balance') as HTMLElement;
    const accountBalanceAmount = accountBalance.querySelector('#account-balance-amount') as HTMLElement;

    const firstTimer = document.querySelector('#first-timer') as HTMLElement;

    const latestQuoteContainer = document.querySelector('#latest-quote-container') as HTMLElement;
    const quoteTotal = document.querySelector('#quote-total') as HTMLElement;
    const quoteSummary = document.querySelector('#quote-summary') as HTMLElement;
    const addToCart = document.querySelector('#add-to-cart-btn') as HTMLButtonElement;
    const prevBtn = document.querySelector('#prev-btn') as HTMLButtonElement;
    const nextBtn = document.querySelector('#next-btn') as HTMLButtonElement;
    const proofEls: Element[] = [];

    const orderHistory = document.querySelector('#order-history') as HTMLElement;
    const orderHistoryCount = orderHistory.querySelector('#order-history-count') as HTMLElement;
    const orderHistoryTotal = orderHistory.querySelector('#order-history-total') as HTMLElement;

    const unpaidEl = document.querySelector('#unpaid') as HTMLElement;
    const unpaidCountEl = unpaidEl.querySelector('#unpaid-count') as HTMLElement;
    const unpaidTotalEl = unpaidEl.querySelector('#unpaid-total') as HTMLElement;
    const unpaidPayEl = unpaidEl.querySelector('#unpaid-pay') as HTMLElement;

    const showCards: HTMLElement[] = [];

    const sess = session.Session();

    void sess.then(sess => {
        if (Big(sess.balance).gt('0')) {
            accountBalanceAmount.textContent = DollarFormat(sess.balance);
            showCards.push(accountBalance);
        }
    });

    const b = new Batch();

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            if (lineItem.id) {
                m.set(lineItem.id, lineItem);
            }
        }
        return m;
    });

    void b.get<hyejoo.Quotes>('/v2/quotes', {
        params: {
            'sort': 'created desc',
            'max': 1,
        },
    }).then(async resp => {
        const latestQuote = resp.data.quotes[0];

        if (!latestQuote) {
            return;
        }

        const viewQuoteBtn = document.querySelector('#view-quote-btn') as HTMLAnchorElement;
        viewQuoteBtn.href = `${Relref('/quote')}?id=${latestQuote.key || ''}`;

        addToCart.addEventListener('click', () => {
            buttons.Start(addToCart);
            void carts.AddCartItem({ quoteID: latestQuote?.id ?? '' }).then(() => {
                addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                addToCart.classList.add('btn-outline-success');

                addToCart.querySelector('i')?.classList.remove('fa-shopping-cart');
                addToCart.querySelector('i')?.classList.add('fa-check');
            }).finally(() => buttons.Stop(addToCart));
        });

        latestQuoteContainer.classList.remove('d-none');

        if (latestQuote.quoteLineItems?.length) {
            const qtys: Record<string, number> = {};
            for (const quoteLineItem of latestQuote.quoteLineItems) {
                if (!quoteLineItem.quantity) {
                    continue;
                }

                const lineItem = (await lineItems).get(quoteLineItem.lineItemID || '');

                if (!(quoteLineItem.lineItem || lineItem?.product)) {
                    continue;
                }

                const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
            }
            quoteSummary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
        }

        if (latestQuote.quoteProofs?.length) {
            for (const op of latestQuote.quoteProofs) {
                const p = op.proof;
                if (!p) {
                    continue;
                }
                if (latestQuote.quoteProofs?.length > 1) {
                    prevBtn.classList.remove('d-none');
                    nextBtn.classList.remove('d-none');
                }

                const name = op.number;

                const thumbnailContainer = document.querySelector('#thumbnail-container') as HTMLElement;
                const thumbEl = template.Clone('#thumbnail-template').firstElementChild as HTMLElement;
                proofEls.push(thumbEl);
                const quoteThumbnail = thumbEl.querySelector('.quote-proof-thumbnail') as HTMLImageElement;
                const thumbAnchor = thumbEl.querySelector('.thumb-anchor') as HTMLAnchorElement;

                const urls = GetProofURLs(window.cloudFront, p.file || '', p.path || '');

                thumbAnchor.href = urls.main;
                thumbAnchor.dataset.caption = `Proof ${name || ''}`;
                thumbAnchor.setAttribute('data-fancybox', latestQuote.id || '');

                quoteThumbnail.src = urls.thumb;
                quoteThumbnail.alt = `Proof ${name || ''}`;
                quoteThumbnail.title = `Proof ${name || ''}`;

                thumbnailContainer.appendChild(thumbEl);

                let currProof = 0;

                const showHideProofs = () => {
                    for (let i = 0; i < proofEls.length; i++) {
                        if (i === currProof) {
                            proofEls[i].classList.remove('d-none');
                        } else {
                            proofEls[i].classList.add('d-none');
                        }
                    }
                };
                nextBtn.addEventListener('click', () => {
                    currProof++;
                    if (currProof >= proofEls.length) {
                        currProof = 0;
                    }
                    showHideProofs();
                });
                prevBtn.addEventListener('click', () => {
                    currProof--;
                    if (currProof < 0) {
                        currProof = proofEls.length - 1;
                    }
                    showHideProofs();
                });
                showHideProofs();
            }
        }
        quoteTotal.textContent = DollarFormat(latestQuote.cachedTotal || '');
    });

    const ordersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => {
        if (!resp.data.count) {
            return;
        }

        orderHistoryCount.textContent = resp.data.count.toLocaleString();
        showCards.push(orderHistory);
    });

    const ordersTotal = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'total',
        },
    }).then(resp => {
        if (!resp.data.sum) {
            return;
        }

        orderHistoryTotal.textContent = DollarFormat(resp.data.sum);
        showCards.push(orderHistory);
    });

    const unpaidParams = {
        'q': 'balance:>=.01',
        'data': 'balance',
        'max': 9999,
    };
    const unpaidOrders = b.get<hyejoo.Orders>('/orders', {
        params: unpaidParams,
    }).then(resp => {
        if (!resp.data.orders.length) {
            return;
        }

        const ids: string[] = [];
        let total = Big('0');

        const consumeResp = (resp: AxiosResponse<hyejoo.Orders>) => {
            total = Big('0');
            for (const order of resp.data.orders) {
                ids.push(order.id || '');
                total = total.add(order.balance || '0');
            }

            unpaidCountEl.textContent = `${resp.data.orders.length.toLocaleString()} ${resp.data.orders.length === 1 ? 'Order' : 'Orders'}`;
            unpaidTotalEl.textContent = DollarFormat(total);
        };

        consumeResp(resp);

        unpaidPayEl.addEventListener('click', () => payments.PromptPayment(undefined, ids, total, false).
            then(() => axios.get<hyejoo.Orders>('/orders', {
                params: unpaidParams,
            }).then(resp => {
                consumeResp(resp);
                if (total.lt('.01')) {
                    unpaidEl.parentElement?.removeChild(unpaidEl);
                }
            })));

        showCards.push(unpaidEl);
    });

    await b.Exec();

    await sess;
    await ordersCount;
    await ordersTotal;
    await unpaidOrders;

    if (!showCards.length) {
        firstTimer.classList.remove('d-none');
    } else {
        showCards.forEach(el => el.classList.remove('d-none'));
    }

    loader.parentElement?.removeChild(loader);
}